// QuotaResponse — mirrors the /quota BE contract (recomputed per request, not cached).
// Fields the FE keys off for the 5-state billing UX (PLT-3994, PLT-3721, PLT-3715, PLT-4027).

export type BlockedReason =
  | 'trial-end'
  | 'overage-cap-reached'
  | 'payment-failed'
  | 'subscription-canceled';

/** Value-const for the {@link BlockedReason} union (for comparisons/switches). */
export const BLOCKED_REASONS = {
  trialEnd: 'trial-end',
  overageCapReached: 'overage-cap-reached',
  paymentFailed: 'payment-failed',
  subscriptionCanceled: 'subscription-canceled',
} as const satisfies Record<string, BlockedReason>;

export interface TrialInfo {
  active: boolean;
  days_remaining: number;
  day_number: number;
  total_days: number;
}

export interface PlanInfo {
  name: string;
  credits_granted: number;
  credits_remaining: number;
}

export interface OverageInfo {
  active: boolean;
  credits: number;
  cap: number | null;
  used: number;
}

export interface BlockedInfo {
  reason: BlockedReason;
}

export interface BillingCycle {
  start: string;
  end: string;
}

export interface QuotaResponse {
  trial: TrialInfo | null;
  plan: PlanInfo | null;
  overage: OverageInfo | null;
  blocked: BlockedInfo | null;
  cycle: BillingCycle | null;
  /** 0..100 — the field the FE keys the banner off (Shlomo: "API returns %, FE decides") */
  usage_pct: number;
}

/**
 * The five self-serve billing lifecycle states. Used as discriminator values
 * across billingState/BillingHero — referenced via the const, never as bare
 * string literals (review: "strings are frail").
 */
export const BILLING_STATES = {
  trialEarly: 'trial-early',
  trialEnding: 'trial-ending',
  healthy: 'healthy',
  approaching: 'approaching',
  overage: 'overage',
} as const;

/**
 * Account tier names from /account-info — the lower-cased UserTierLevel on the
 * BE (free/trial/paid/enterprise). A closed const so callers branch on a typed
 * value, not a bare string (review: "define a const of tiers, not str").
 */
export const ACCOUNT_TIERS = {
  free: 'free',
  trial: 'trial',
  paid: 'paid',
  enterprise: 'enterprise',
} as const;

export type AccountTier = (typeof ACCOUNT_TIERS)[keyof typeof ACCOUNT_TIERS];

/** Stripe coupon perk granted during a migration campaign. */
export interface MigrationPerk {
  active: boolean;
  percentOff: number;
  durationMonths: number;
}

/** Conversion record states written by the migration scripts. */
export const CONVERSION_STATES = {
  trialActive: 'trial_active',
  awaitingMove: 'awaiting_move',
  // Paid Move parked mid-flight: the off-session charge needs 3DS, so the account
  // is awaiting the user's reversal consent (still legacy until they agree).
  paymentActionRequired: 'payment_action_required',
  // The user agreed to the reversal: legacy sub canceled + refunded, account now
  // free self-serve. Terminal — but until they actually buy a self-serve plan we
  // keep nudging them into checkout (they still carry the decaying coupon).
  reversed: 'reversed',
} as const;

export type ConversionState =
  (typeof CONVERSION_STATES)[keyof typeof CONVERSION_STATES];

/**
 * Conversion cohort values identifying the migration path.
 * `free` — free-tier accounts getting a trial.
 * `annual` / `monthly` — paid-seat accounts invited to move to usage-based.
 */
export const CONVERSION_COHORTS = {
  free: 'free',
  annual: 'annual',
  monthly: 'monthly',
} as const;

export type ConversionCohort =
  (typeof CONVERSION_COHORTS)[keyof typeof CONVERSION_COHORTS];

// AccountInfoResponse — mirrors GET /billing/v1/account-info (PLT-4042).
// camelCase to match the BE CamelModel wire format (Q5). Trial + account-level
// data live here, sourced from the Firestore account/workspace docs — a
// different data domain than the consumption-backed /quota (standup D22).
export interface AccountInfoResponse {
  selfServe: boolean;
  tier: AccountTier | null;
  stripeCustomerId: string | null;
  trial: {
    active: boolean;
    daysRemaining: number;
    dayNumber: number;
    totalDays: number;
  } | null;
  // Whether THIS caller may upgrade / manage the plan — the same authority the
  // money-path routes enforce (PLT-4305). Optional + treated as false when
  // absent so the FE fails closed against an older backend.
  canManageBilling?: boolean;
  billingAdminEmail?: string | null;
  // Whether the account is on usage-based billing (an active, non-trial credit
  // cycle), computed server-side (PLT-4444). Gates the Extended mode toggle.
  // Optional and treated as false when absent so the FE fails closed against an
  // older backend.
  usageBased?: boolean;
  // Flat conversion fields — populated for accounts in a migration flow.
  // `conversionCohort` sub-classifies the path (free / annual / monthly).
  // `conversionState` drives UI gates; `awaiting_move` = invited, not yet acted.
  // `selfServe: true` means the account already moved — suppress the offer.
  conversionState?: ConversionState;
  conversionCohort?: ConversionCohort;
  trialBannerShown?: boolean;
  migrationPerk?: MigrationPerk;
  migrationStartDate?: string;
  migrationEndDate?: string;
}

/**
 * A usage-based plan recommendation surfaced on the trial hero / conversion
 * popup (auto-calculator, PLT-4059). Null until enough usage has accrued.
 */
export interface PlanRecommendation {
  /** Display label of the recommended SKU, e.g. "$210 / mo". */
  planName: string;
  /** Monthly credit pool of the recommended SKU. */
  credits: number;
  /** Number of trial days the recommendation is based on. */
  basedOnDays: number;
}

// Wire shape of GET /billing/v1/recommendation. `available` is false until
// there's usage signal; `recommendedPlan` is null when no plan is recommended.
export interface RecommendationResponse {
  available: boolean;
  basedOnDays: number;
  monthlyCredits: number;
  recommendedPlan: { credits: number; monthlyCostUsd: number } | null;
  enterpriseHandoff: boolean;
}

export type BillingStateKey =
  (typeof BILLING_STATES)[keyof typeof BILLING_STATES];

/**
 * Alert kinds derived from a quota snapshot. Banner-eligible subset is decided
 * by isBannerEligible (deriveAlert.ts); trial alerts are page-hero-only (D13).
 */
export const ALERT_TYPES = {
  trialActive: 'trial_active',
  trialEnding: 'trial_ending',
  percentageThreshold: 'percentage_threshold',
  overageActive: 'overage_active',
  blocked: 'blocked',
} as const;

export type AlertType = (typeof ALERT_TYPES)[keyof typeof ALERT_TYPES];

export interface Alert {
  type: AlertType;
  value: number | string | null;
}
